<script lang="ts">
  import { fade } from "svelte/transition";
  import { isUserAuthed, session } from "app/stores/auth";
  import { community, communitySlug } from "app/stores/community";
  import { Copy, Refresh, SearchText } from "$components/+blocks/OVIcon";
  import { createEventDispatcher, onMount } from "svelte";
  import { marked } from "marked";
  import { debounce } from "app/utils";
  import { saveToClipboard } from "app/utils/clipboard";
  import { alerts } from "app/stores/alert";
  import { streamResponse } from "$components/ai/search/utils";
  import { docURL } from "$utils/urls";
  import { slugify } from "$utils/strings";
  import { tooltip } from "app/actions/tooltip";
  import Avatar from "$components/+blocks/Avatar/Avatar.svelte";
  import OVIcon from "$components/+blocks/OVIcon/OVIcon.svelte";
  import Reader from "$components/editor/Reader.svelte";
  import IconButton from "$components/+blocks/IconButton/IconButton.svelte";
  import VisitorAvatar from "./VisitorAvatar.svelte";
  import FeedbackButton from "$components/knowledge-base/DocFeedback/FeedbackButton.svelte";
  import api from "api";
  import type { Document as ApiDocument } from "@outverse-video/outverse-api-client-lib";

  export let prompt: string;
  export let conversationID: string;
  export let refreshEnabled: boolean = false;
  export let autoScroll = true;
  export let processing: boolean = false;

  let analysing: boolean = true;
  let resultId: string;
  let dispatch = createEventDispatcher();
  let ref: HTMLDivElement;
  let totalString = "";
  let isScrolling = false;
  let sources: Array<ApiDocument> = [];
  let feedbackState: "positive" | "negative" | null = null;

  const handleFeedback = (type: "positive" | "negative") => {
    if (feedbackState === type) return; // do nothing if the same button is clicked again
    feedbackState = type;

    api.communitiesChatEvaluateTrace({
      communitySlug: $communitySlug,
      sessionId: conversationID,
      traceId: resultId!,
      chatFeedback: {
        isPositive: type === "positive",
      },
    });
  };

  function handleDebouncedScroll() {
    const debouncedScroll = debounce(() => {
      ref?.scrollIntoView({ behavior: "smooth", block: "end" });
      isScrolling = false;
    }, 400);

    // scrolli f it is not already scrolling and the user has not yet scrolled
    if (!isScrolling && autoScroll) {
      isScrolling = true;
      debouncedScroll();
    }
  }

  $: {
    // auto scroll whenever the content changes
    handleDebouncedScroll();
    totalString;
  }

  async function gatherResponse() {
    const generator = streamResponse(prompt, [], $communitySlug, conversationID);
    for await (const chunk of generator) {
      if (!processing) {
        break;
      }

      // rewrite to only do this if chunk is of type answer
      if (chunk.hasOwnProperty("metadata")) {
        const metadata = chunk["metadata"];
        if (metadata.hasOwnProperty("TRACE_ID")) {
          resultId = metadata["TRACE_ID"];
        }
        if (metadata.hasOwnProperty("sources")) {
          sources = metadata["sources"];
        }
      } else if (chunk.hasOwnProperty("answer")) {
        analysing = false;
        totalString += chunk["answer"];
      } else if (chunk.hasOwnProperty("error")) {
        alerts.error("An error occurred");
        break;
      } else if (chunk.hasOwnProperty("heartbeat")) {
        console.log("generating answer...");
      } else {
        console.log(`unhandled type ${JSON.stringify(chunk)}`);
      }
    }

    dispatch("stop-processing");
  }
  onMount(gatherResponse);

  // replyLink is used to navigate to the thread suggested by the AI
  // $: replyLink = replyUrl({
  //   communitySlug: $communitySlug,
  //   spaceUuid: mockedResult.reply.parentThread.forumSpace.uuid,
  //   spaceSlug: slugify(mockedResult.reply.parentThread.forumSpace.title),
  //   threadUuid: mockedResult.reply.parentThread.uuid,
  //   threadSlug: slugify(mockedResult.reply.parentThread.title),
  //   replySeqNum: mockedResult.reply.seqNum.toString(),
  // });
</script>

<div class="p-4 pb-0" in:fade>
  <div class="mt-3 flex items-start gap-3">
    {#if $isUserAuthed}
      <Avatar
        mainImage={$session?.user?.profileImage}
        label={$session?.user?.handle?.charAt(0)}
        size="sm:size-6 size-5"
        radius="rounded-[3px] sm:rounded-md"
        wrapperClasses="mt-0.5 sm:mt-0"
      />
    {:else}
      <VisitorAvatar size="sm:size-6 size-5" customIconSize="sm:size-3 size-2.5" />
    {/if}
    <span class="mt-0.5 text-sm font-medium text-gray-700 dark:text-white">
      {prompt}
    </span>
  </div>

  <div bind:this={ref} class="pb-4">
    <div class="mt-8 flex flex-col gap-3">
      <div class="flex items-start gap-3">
        <Avatar
          mainImage={$community.logoImage}
          backgroundColor={$community.primaryColor}
          size="sm:size-6 size-5"
          radius="rounded-[3px] sm:rounded-md"
          label={$community.name?.[0]}
          textSize="text-xs"
          wrapperClasses="mt-0.5 sm:mt-0"
        />
        {#if analysing && processing}
          <div class="mt-2 size-2 animate-pulse rounded-full bg-neutral-600 dark:bg-neutral-500" />
        {/if}
        {#if !analysing}
          <div class="flex w-full flex-col gap-1 pr-7">
            <!-- AI response, compiles markdown to html -->
            <Reader content={marked(totalString)} disableLinkHandle />
            <div class="flex justify-between">
              <div class="flex items-center gap-0.5 opacity-70">
                <IconButton
                  variant="tertiary"
                  icon={Copy}
                  a11yLabel="Copy answer"
                  on:click={() =>
                    saveToClipboard(totalString, alerts.add, "Answer copied to clipboard")}
                />
                {#if refreshEnabled}
                  <IconButton
                    variant="tertiary"
                    icon={Refresh}
                    a11yLabel="Regenerate answer"
                    on:click={() => dispatch("refresh")}
                  />
                {/if}
              </div>
              {#if $isUserAuthed}
                <!-- feedback buttons -->
                <div class="flex w-fit items-center justify-center gap-1 p-2">
                  <p class="mr-1 text-xs font-medium text-neutral-400">Was this helpful?</p>
                  <FeedbackButton
                    isPositive
                    hideLabel
                    clicked={feedbackState === "positive"}
                    on:click={() => handleFeedback("positive")}
                  />
                  <FeedbackButton
                    hideLabel
                    clicked={feedbackState === "negative"}
                    on:click={() => handleFeedback("negative")}
                  />
                </div>
              {/if}
            </div>
          </div>
        {/if}
      </div>

      <!-- reply -->
      <!-- {#if mockedResult.reply && !analysing}
      <div class="mb-4 mt-2 flex flex-col gap-3 px-[28px] sm:px-[36px]">
        <span class="text-[13px] text-gray-800 dark:text-gray-100">
          In a community thread a user also asked about this and received the following reply that
          might be useful:
        </span>
        <div
          class="relative cursor-pointer overflow-hidden rounded-md border-[0.5px] border-black/10 p-3 transition-colors hover:bg-gray-200 dark:border-white/15 dark:hover:bg-white/5"
          on:click={() => goto(replyLink)}
          on:keydown={() => goto(replyLink)}
          use:autoAnimate={{ duration: 170 }}
        >
          <div class="flex justify-between gap-1">
            <div
              class="active:border-community-primary inline-flex items-center gap-1 whitespace-nowrap rounded border-[0.5px] border-transparent bg-gray-300/40 px-1 py-0.5 text-xs font-medium text-gray-500 transition-colors hover:bg-gray-300/80 dark:bg-white/5 dark:text-gray-300 dark:hover:bg-white/10 dark:hover:text-gray-100"
            >
              <OVIcon icon={ReplyingTo} class="inline-block" size="size-3" />
              Replying to {mockedResult.reply.threadTitle}
            </div>
            {#if mockedResult.reply.solved}
              <div class="flex items-center gap-1 rounded bg-emerald-400/20 p-1 pl-[3px]">
                <OVIcon icon={CheckFilled} class="text-emerald-400" size="size-3.5" />
                <span class="text-xs font-medium leading-[0] text-emerald-900 dark:text-emerald-200"
                  >Solved
                </span>
              </div>
            {/if}
          </div>
          <p
            class="mt-3 line-clamp-4 text-[13px] font-medium leading-[160%] text-gray-500 dark:text-gray-300"
          >
            {mockedResult.reply.text}
          </p>
        </div>
      </div>
    {/if} -->

      <!-- sources -->
      {#if sources?.length && !analysing}
        <div
          class="mx-[28px] flex w-fit flex-col items-start gap-1.5 overflow-hidden rounded-md bg-neutral-100 pr-2 pl-1 text-xs sm:mx-[36px] sm:flex-row dark:bg-white/5 dark:text-neutral-200"
          in:fade
        >
          <div class="mx-1 my-2 flex items-center gap-1">
            <OVIcon icon={SearchText} />
            <p class="mr-1">Sources:</p>
          </div>

          <div class="my-1 flex w-full flex-wrap gap-1.5 overflow-hidden">
            {#each sources as source (source.uuid)}
              <a
                href={docURL({
                  communitySlug: $communitySlug,
                  docUuid: source.uuid,
                  docSlug: slugify(source.title || ""),
                })}
                on:click|stopPropagation
                target="_blank"
                title={source.title}
                use:tooltip
                class="ov-focus flex items-center gap-1.5 overflow-hidden rounded-md border-[0.5px] border-transparent p-1 after:rounded-md hover:border-black/10 hover:bg-white dark:hover:border-transparent dark:hover:bg-neutral-800"
              >
                <Avatar mainImage={$community.logoImage} radius="rounded" />
                <span class="truncate sm:max-w-[18ch]">{source.title}</span>
              </a>
            {/each}
          </div>
        </div>
      {/if}
    </div>
  </div>
</div>
